import type { ProjectDocument, WallPlacement } from '@bw/document';

/** How long a wall to leave room for when the one before it has no bricks. */
const ASSUMED_LENGTH_MM = 3000;

/**
 * Where a newly added wall should go.
 *
 * Every wall used to be created at the same default placement, so adding one
 * dropped it exactly on top of the wall you already had: the plan showed a
 * single wall, the overlap warning fired, and nothing explained why. That is
 * most of "it is not clear how to add a second wall and lay it out".
 *
 * A new wall instead starts where the current one ends, turned a quarter turn
 * the same way each time, so adding walls in order walks anticlockwise round a
 * rectangle — and four walls of equal length come back exactly to the first
 * wall's origin, which closes the outline with no nudging at all.
 *
 * The wall's own frame is: local +x along its length, +z through its
 * thickness, outer face at z = 0, and world = Ry(rotationDeg) · local + origin.
 *
 * The join is made on the CENTRELINES, not on the origin corners, because the
 * footprint — and therefore the roof — is built from centrelines. Chaining
 * corner to corner instead leaves every join half a thickness out, which is
 * within nothing: the ring stays open and no roof is ever offered.
 */
export function placementForNewWall(project: ProjectDocument): WallPlacement {
  const previous = project.activeWall;
  const bounds = previous.model.bounds();
  const length = bounds === null || bounds.maxX <= 0 ? ASSUMED_LENGTH_MM : bounds.maxX;
  const placement = previous.placement;
  const radians = (placement.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const half = placement.thicknessMm / 2;

  // Ry(θ) · (x, 0, z) = (x·cosθ + z·sinθ, −x·sinθ + z·cosθ). The far end of
  // this wall's centreline is that applied to (length, 0, half)...
  const endX = placement.origin.x + length * cos + half * sin;
  const endZ = placement.origin.z - length * sin + half * cos;

  // ...and the new wall, turned a further 90°, must start its own centreline
  // there. Its start sits half a thickness off its origin along its own +z,
  // which after the turn points back along this wall's length.
  const turned = radians + Math.PI / 2;
  return {
    origin: {
      x: round(endX - half * Math.sin(turned)),
      y: placement.origin.y,
      z: round(endZ - half * Math.cos(turned)),
    },
    rotationDeg: normaliseDegrees(placement.rotationDeg + 90),
    thicknessMm: placement.thicknessMm,
  };
}

/** Placements are millimetres; a floating-point tail helps nobody read them. */
function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function normaliseDegrees(value: number): number {
  return ((value % 360) + 360) % 360;
}
